// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/services/game/GameArt.tsx
// 🔧 PURPOSE: I disegni della pagina del Memory (mockup "SvcGioco"): le tre
//             carte a ventaglio della testata, le figure delle quattro
//             coppie (al posto delle emoji di prima), i coriandoli della
//             vittoria e le due carte tratteggiate di "Presto altri giochi".
//             Solo decorazione: aria-hidden ovunque.
// ============================================

import React from 'react';
import { cn } from '../../../lib/utils';
import type { MemoryKind } from './useMemoryGame';

/** Le figure delle coppie, disegnate sul riquadro 24x24 (stile lucide). */
export const CARD_PATHS: Record<MemoryKind, string> = {
  pizza: 'M15 11h.01M11 15h.01M16 16h.01M2 16l20 6-6-20A20 20 0 0 0 2 16zM5.71 17.11a17.04 17.04 0 0 1 11.4-11.4',
  pasta: 'M3 12h18a9 8 0 0 1-18 0zM7 12c0-1.5 2-2.5 2-4S7 5.5 7 4M11 12c0-1.5 2-2.5 2-4s-2-2.5-2-4M15 3v3a2 2 0 0 0 4 0V3M17 3v9',
  wine: 'M8 22h8M7 10h10M12 15v7M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5z',
  beach: 'M3 11a9 8 0 0 1 18 0zM12 3c-2 2-3 5-3 8M12 3c2 2 3 5 3 8M12 11v8M12 3V2M2 21c1.7-1 3.3-1 5 0s3.3 1 5 0 3.3-1 5 0 3.3 1 5 0',
};

/** La figura di una coppia; il colore arriva da `className` (currentColor). */
export const CardGlyph: React.FC<{ kind: MemoryKind; className?: string }> = ({ kind, className }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('h-8 w-8 flex-shrink-0', className)}
  >
    <path d={CARD_PATHS[kind]} />
  </svg>
);

/**
 * La testata: tre carte a ventaglio (una coperta, vino e pizza scoperti) e
 * qualche scintilla, a destra come nel mockup. Sotto i 360px si rimpicciolisce
 * un po' per lasciare spazio al titolo.
 */
export const GameHeroArt: React.FC = () => (
  <svg
    width="196"
    height="132"
    viewBox="0 0 196 132"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="absolute right-3 top-[58px] origin-top-right max-[359px]:scale-[0.8]"
  >
    <path d="M14 22l2.5-6 2.5 6-2.5 6zM180 104l2-5 2 5-2 5zM186 18h.01M8 96h.01" stroke="#FFFFFF" strokeWidth="2" strokeOpacity=".8" />
    <g transform="translate(50 72) rotate(-15)">
      <rect x="-29" y="-39" width="58" height="78" rx="9" fill="rgba(255,255,255,.18)" stroke="#FFFFFF" strokeWidth="2" strokeOpacity=".85" />
      <rect x="-23" y="-33" width="46" height="66" rx="6" stroke="#FFFFFF" strokeWidth="1.4" strokeOpacity=".45" />
      <text x="0" y="11" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontSize="30" fontWeight="700" fill="#FFFFFF">
        ?
      </text>
    </g>
    <g transform="translate(98 62) rotate(2)">
      <rect x="-29" y="-39" width="58" height="78" rx="9" fill="#FFFDF9" stroke="#FFFFFF" strokeWidth="2" />
      <g transform="translate(-15.6 -17) scale(1.3)" stroke="#6D28D9" strokeWidth="1.8">
        <path d={CARD_PATHS.wine} />
      </g>
    </g>
    <g transform="translate(146 74) rotate(16)">
      <rect x="-29" y="-39" width="58" height="78" rx="9" fill="#FFFDF9" stroke="#FFFFFF" strokeWidth="2" />
      <g transform="translate(-15.6 -17) scale(1.3)" stroke="#6D28D9" strokeWidth="1.8">
        <path d={CARD_PATHS.pizza} />
      </g>
    </g>
  </svg>
);

/** I coriandoli sulla card della vittoria (358x120 a 390px; più stretta si rimpicciolisce, più larga resta al centro). */
export const WinConfetti: React.FC = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 358 120"
    preserveAspectRatio="xMidYMin meet"
    className="pointer-events-none absolute inset-x-0 top-0 h-[120px] w-full"
  >
    <rect x="34" y="22" width="10" height="5" rx="2" fill="#C4B5FD" transform="rotate(-24 39 24)" />
    <rect x="300" y="30" width="10" height="5" rx="2" fill="#C4A24C" transform="rotate(30 305 32)" />
    <rect x="70" y="78" width="9" height="4" rx="2" fill="#FFFFFF" fillOpacity=".7" transform="rotate(18 74 80)" />
    <rect x="276" y="84" width="9" height="4" rx="2" fill="#C4B5FD" transform="rotate(-30 280 86)" />
    <circle cx="104" cy="30" r="3" fill="#C4A24C" />
    <circle cx="258" cy="18" r="3" fill="#FFFFFF" fillOpacity=".75" />
    <circle cx="26" cy="92" r="2.5" fill="#FFFFFF" fillOpacity=".6" />
    <circle cx="332" cy="70" r="2.5" fill="#C4A24C" />
    <path d="M318 104l2-5 2 5-2 5zM52 50l2-5 2 5-2 5z" fill="#FFFFFF" fillOpacity=".8" />
  </svg>
);

/** Le due carte tratteggiate (una col "?") di "Presto altri giochi". */
export const SoonCards: React.FC = () => (
  <svg aria-hidden="true" width="58" height="46" viewBox="0 0 58 46" fill="none" className="flex-shrink-0 text-violet-700 dark:text-violet-300">
    <rect
      x="6"
      y="6"
      width="26"
      height="34"
      rx="6"
      strokeWidth="1.6"
      strokeDasharray="3 3"
      transform="rotate(-10 19 23)"
      className="stroke-violet-300 dark:stroke-violet-700"
    />
    <rect
      x="26"
      y="5"
      width="26"
      height="34"
      rx="6"
      strokeWidth="1.6"
      strokeDasharray="3 3"
      transform="rotate(8 39 22)"
      className="fill-violet-50 stroke-violet-400 dark:fill-violet-950/60 dark:stroke-violet-500"
    />
    <path
      d="M36.5 18.5a2.5 2.5 0 1 1 3.2 2.4c-.5.2-.7.6-.7 1.1v.5M39 26.5h.01"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      transform="rotate(8 39 22)"
    />
  </svg>
);
